import { AfterFirstDigitRegex, moneyPatternRegex } from "../../constants/regex";
import { Currency } from "../../constants/money";
import { ChangeEvent, FocusEvent } from "react";

const validateCurrency = (currency: string) =>
  (currency || "USD").toUpperCase();

export const DollarFormatter = function (currency = "usd"): Intl.NumberFormat {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: validateCurrency(currency),
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};

export const PennyDollarFormatter = function (
  currency = "usd",
): Intl.NumberFormat {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: validateCurrency(currency),
    minimumFractionDigits: 2,
  });
};

// This formatter forces the display string to have the following format, regardless of the user's locales
// E.g: 1234 -> $1,234
export const DollarNarrowFormatter = function (
  currency = "USD",
): Intl.NumberFormat {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: validateCurrency(currency),
    currencyDisplay: "narrowSymbol",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};

interface ValidateMoneyValueParams {
  value: string;
  wholeDigitsOnly?: boolean;
  currency?: string;
  min?: number;
  max?: number;
}

export const DEFAULT_CURRENCY = Currency.USD;

export const getEmptyMoney = (currency?: string) =>
  DollarFormatter(currency || DEFAULT_CURRENCY)
    .format(0)
    .replace(AfterFirstDigitRegex, "");

export const validateMoneyValue = ({
  value,
  wholeDigitsOnly,
  currency,
  min,
  max = 999999999999999,
}: ValidateMoneyValueParams) => {
  const emptyMoney = getEmptyMoney();
  // Remove emptyMoney pattern and all commas
  let newValue = value.replace(emptyMoney, "").replace(/,/g, "");

  if (wholeDigitsOnly) {
    // Strip out any decimals and everything after
    newValue = newValue.split(".")[0];
  }

  // Always remove leading zeros from the integer part
  if (newValue.includes(".")) {
    // Split into integer and decimal, strip leading zeros from integer part only
    const [intPart, decPart] = newValue.split(".");
    newValue =
      intPart.replace(/^0+(?=\d)/, "") +
      (wholeDigitsOnly ? "" : "." + (decPart.slice(0, 2) ?? ""));
  } else {
    newValue = newValue.replace(/^0+(?=\d)/, "");
  }

  // Only one decimal allowed, and not at the start (unless it's ".5" etc.)
  const hasDecimal = (newValue.match(/\./g) || []).length === 1;
  const addingDecimal = newValue.endsWith(".") && hasDecimal;

  // Check for valid money pattern, or allow if user is typing a decimal
  if (!moneyPatternRegex.test(newValue) && !addingDecimal) {
    if (newValue === "") return "";

    // Remove last character (not always correct if multiple invalids)
    return value.slice(0, -1);
  }

  const originalParsedFloat = parseFloat(newValue);
  let parsedFloat = originalParsedFloat;

  // If not a number, empty the value so we can see the placeholder
  if (Number.isNaN(parsedFloat)) {
    return "";
  }

  // Clamp to min/max
  if (min && parsedFloat < min) {
    parsedFloat = min;
  } else if (max && parsedFloat > max) {
    parsedFloat = max;
  }

  // If only cents are being edited, return as-is
  if (
    !wholeDigitsOnly &&
    Math.round(originalParsedFloat) === Math.round(parsedFloat) &&
    hasDecimal
  ) {
    // Remove emptyMoney prefix and leading zeros between it and the first non-zero digit
    let cleaned = value.replace(emptyMoney, "");
    const [intPart, decPart] = cleaned.split(".");
    cleaned =
      intPart.replace(/^0+(?=\d)/, "") + "." + (decPart.slice(0, 2) ?? "");
    return emptyMoney + cleaned;
  }

  // Pick formatter
  let formatter = DollarNarrowFormatter;
  if (!wholeDigitsOnly && hasDecimal) {
    formatter = PennyDollarFormatter;
  }

  return formatter(currency).format(parsedFloat);
};

export type OnChangeOrOnBlurMoneyFieldEvent =
  | ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  | FocusEvent<HTMLInputElement | HTMLTextAreaElement>;

export const onChangeOrOnBlurMoneyField = (
  e: OnChangeOrOnBlurMoneyFieldEvent,
  options: Omit<ValidateMoneyValueParams, "value"> = {},
) => {
  const originalValue = e.target.value;
  const newValue = validateMoneyValue({
    value: originalValue,
    ...options,
  });
  if (originalValue !== newValue) {
    e.target.value = newValue;
  }
};
